/* eslint-disable react/no-array-index-key */
/* eslint-disable jsdoc/check-param-names */
/* eslint-disable jsdoc/check-types */
/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/no-undefined-types */
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Paper,
  Alert,
  Tooltip,
} from "@mui/material";
import { supabase } from "../../../../../supabase";

/**
 * @description HierarchyCard component displays the parent manager of the selected user along with the upstream hierarchy.
 * @param {Object} parent - The parent object of the selected user.
 * @param {Array} upStream - The upstream manager hierarchy of the selected user.
 * @param {Function} onChangeParentClick - The click handler for changing the parent manager.
 * @param {Object} dealer - The dealer object of the selected user.
 * @returns {ReactElement} The HierarchyCard component.
 */
export default function HierarchyCard({
  parent,
  upStream,
  onChangeParentClick,
  dealer,
}) {
  const [hasValidParents, setHasValidParents] = React.useState(true);

  // Check if there are any valid parents available using closure table
  React.useEffect(async () => {
    if (!dealer) {
      setHasValidParents(false);
      return;
    }

    try {
      // Get the dealer's downstream hierarchy from views_users_closure
      const { data: closureData, error: closureError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("views_users_closure")
        .select("descendant_id")
        .eq("ancestor_id", dealer.id);

      if (closureError) {
        setHasValidParents(false);
        return;
      }

      // Extract descendant IDs (the dealer's downstream hierarchy)
      const dealerDownstreamIds = closureData.map(row => row.descendant_id);

      // Check if there are any leaders available excluding dealer and their downstream
      const { data: availableLeaders, error: leadersError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_master")
        .select("id")
        .eq("is_leader", true)
        .neq("id", dealer.id)
        .not("id", "in", `(${dealerDownstreamIds.join(",")})`)
        .neq("refs_user_lifecycle_status_id", 4)
        .limit(1); // We only need to know if any exist

      if (leadersError) {
        setHasValidParents(false);
        return;
      }

      setHasValidParents(availableLeaders && availableLeaders.length > 0);
    } catch (error) {
      setHasValidParents(false);
    }
  }, [dealer]);

  return (
    <Card elevation={2} sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Hierarchy
          </Typography>
          {hasValidParents ? (
            <Button
              size="small"
              variant="outlined"
              onClick={onChangeParentClick}
              sx={{ borderRadius: 2 }}
            >
              Change Manager
            </Button>
          ) : (
            <Tooltip title="No valid managers available - all eligible managers are in this user's downstream hierarchy">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled
                  sx={{ borderRadius: 2 }}
                >
                  Change Manager
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        {!hasValidParents && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            No valid managers available for reassignment.
          </Alert>
        )}

        {!parent ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            User does not have a parent.
          </Alert>
        ) : upStream && upStream.length > 0 ? (
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              Parent Structure:
            </Typography>
            {upStream.map((item, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Z-{item.id}: {item?.first_name}
                  {item?.middle_name ? ` ${item.middle_name}` : ""}{" "}
                  {item?.surname}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            No Parent Found
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
